import { prepareFluidImageResize } from './editorFluidImageResize.js';
import type { WebEditorElementDefinition } from '../../element-definition.js';
import { editorNodeIdClass } from './dom.js';
import {
  clampEditorFocalPoint,
  clampEditorGridColumns,
  clampEditorGridHeight,
  clampEditorGridWidth,
  clampEditorImageScale,
  editorGridPlacementFromRects,
  editorGridDefaultColumns,
  resolveEditorImageScale,
  type WebEditorGridPlacement,
} from '../../geometry.js';
import type { WebEditorElementFrame, WebEditorLayoutProject } from '../../layout.js';
import type { EditorFrame, EditorPreviewProfileId } from './editorPageConfig.js';
import { editorViewportRect, queryEditorPreviewNode } from './editorPreviewViewport.js';

export type EditorResizeHandle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

export interface GeometryOperationsOptions<P extends WebEditorLayoutProject = WebEditorLayoutProject> {
  project: P;
  definitions: readonly WebEditorElementDefinition[];
  activeFrame: EditorFrame;
  activePreviewProfile: EditorPreviewProfileId;
  cloneProject: (project: P) => P;
  updateProject: (project: P) => void;
}

const viewportProperties = (frame: EditorFrame) =>
  frame === 'desktop'
    ? ({ grid: 'gridDesktop', columns: 'gridColumnsDesktop', container: 'gridContainerDesktop', focus: 'imageFocusDesktop', mask: 'imageMaskDesktop' } as const)
    : ({ grid: 'gridMobile', columns: 'gridColumnsMobile', container: 'gridContainerMobile', focus: 'imageFocusMobile', mask: 'imageMaskMobile' } as const);

type ViewportProperties = ReturnType<typeof viewportProperties>;

const frameFor = (project: WebEditorLayoutProject, elementId: string): WebEditorElementFrame =>
  project.elementFrames[elementId] ?? { x: 0, y: 0 };

const selectedNode = (elementId: string) =>
  queryEditorPreviewNode(`.vp-editor-preview-page .${editorNodeIdClass(elementId)}`);

const reorderGridItems = (
  project: WebEditorLayoutProject,
  properties: ViewportProperties,
  parentId: string,
  elementId: string,
  requestedOrder: number,
) => {
  const ids = Object.entries(project.elementFrames)
    .filter(([id, frame]) => id !== elementId && frame[properties.grid]?.parentId === parentId)
    .sort((first, second) => (first[1][properties.grid]?.order ?? 0) - (second[1][properties.grid]?.order ?? 0))
    .map(([id]) => id);
  ids.splice(Math.min(ids.length, Math.max(0, Math.round(requestedOrder))), 0, elementId);
  ids.forEach((id, order) => {
    const frame = frameFor(project, id);
    const placement = frame[properties.grid];
    if (placement) project.elementFrames[id] = { ...frame, [properties.grid]: { ...placement, order } };
  });
};

const prepareGridProject = <P extends WebEditorLayoutProject>(options: GeometryOperationsOptions<P>, elementId: string) => {
  const properties = viewportProperties(options.activeFrame);
  const definition = options.definitions.find(candidate => candidate.id === elementId);
  const parentId = definition?.parentId;
  const node = selectedNode(elementId);
  if (!parentId || !node) return null;
  const registeredParent = selectedNode(parentId);
  // A grid on a distant logical ancestor would reflow the section without moving the selected node.
  if (!registeredParent || node.parentElement !== registeredParent) return null;
  const parentNode = registeredParent;

  const nextProject = options.cloneProject(options.project);
  const existingFrame = frameFor(nextProject, elementId);
  const parentFrame = frameFor(nextProject, parentId);
  const columns = clampEditorGridColumns(
    existingFrame[properties.columns] ?? parentFrame[properties.columns] ?? editorGridDefaultColumns,
  );
  const parentRect = parentNode.getBoundingClientRect();
  const siblings = options.definitions
    .filter(candidate => candidate.parentId === parentId)
    .map(candidate => ({ definition: candidate, node: selectedNode(candidate.id) }))
    .filter((entry): entry is { definition: WebEditorElementDefinition; node: HTMLElement } => Boolean(entry.node && entry.node.parentElement === parentNode))
    .sort((a, b) => {
      const first = a.node.getBoundingClientRect();
      const second = b.node.getBoundingClientRect();
      return Math.abs(first.top - second.top) > 8 ? first.top - second.top : first.left - second.left;
    });

  nextProject.elementFrames[parentId] = {
    ...parentFrame,
    [properties.container]: true,
    [properties.columns]: columns,
  };
  siblings.forEach((entry, order) => {
    const siblingFrame = frameFor(nextProject, entry.definition.id);
    const existingPlacement = siblingFrame[properties.grid];
    nextProject.elementFrames[entry.definition.id] = {
      ...siblingFrame,
      [properties.columns]: columns,
      [properties.grid]:
        existingPlacement ??
        editorGridPlacementFromRects(
          entry.node.getBoundingClientRect(),
          parentRect,
          columns,
          order,
          parentId,
          entry.definition.kind === 'image',
          entry.definition.kind === 'image'
            ? resolveEditorImageScale(siblingFrame, options.activePreviewProfile) / 100
            : 1,
        ),
    };
  });

  const selectedFrame = frameFor(nextProject, elementId);
  const placement = selectedFrame[properties.grid];
  if (!placement) return null;
  return { nextProject, placement, columns, node, parentNode, parentId, properties };
};

const overlayNode = () => document.querySelector<HTMLElement>('.vp-editor-geometry-overlay');

const autoMaskContext = <P extends WebEditorLayoutProject>(options: GeometryOperationsOptions<P>, elementId: string) => {
  const definition = options.definitions.find(candidate => candidate.id === elementId);
  const hostId = definition?.maskHostId;
  if (!hostId) return null;
  const itemKey = options.activeFrame === 'desktop' ? 'autoLayoutItemDesktop' : 'autoLayoutItemMobile';
  const hostFrame = frameFor(options.project, hostId);
  const item = hostFrame[itemKey];
  const hostNode = selectedNode(hostId);
  return item && hostNode ? { hostId, hostNode, hostFrame, item, itemKey } : null;
};

const beginPointerGesture = (
  event: PointerEvent,
  onMove: (event: PointerEvent) => void,
  onCommit: () => void,
  onCancel: () => void,
) => {
  event.preventDefault();
  event.stopPropagation();
  const target = event.currentTarget as HTMLElement;
  target.setPointerCapture?.(event.pointerId);
  let pendingEvent: PointerEvent | null = null;
  let frameId: number | null = null;
  let finished = false;
  const flush = () => {
    frameId = null;
    if (pendingEvent) onMove(pendingEvent);
    pendingEvent = null;
  };
  const handleMove = (moveEvent: PointerEvent) => {
    pendingEvent = moveEvent;
    frameId ??= window.requestAnimationFrame(flush);
  };
  const cleanup = () => {
    if (frameId !== null) window.cancelAnimationFrame(frameId);
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', commit);
    window.removeEventListener('pointercancel', cancel);
    window.removeEventListener('keydown', keydown);
  };
  const commit = () => {
    if (finished) return;
    finished = true;
    flush();
    cleanup();
    onCommit();
  };
  const cancel = () => {
    if (finished) return;
    finished = true;
    cleanup();
    onCancel();
  };
  const keydown = (keyboardEvent: KeyboardEvent) => {
    if (keyboardEvent.key !== 'Escape') return;
    keyboardEvent.preventDefault();
    cancel();
  };
  window.addEventListener('pointermove', handleMove);
  window.addEventListener('pointerup', commit, { once: true });
  window.addEventListener('pointercancel', cancel, { once: true });
  window.addEventListener('keydown', keydown);
};

export const createEditorGeometryOperations = <P extends WebEditorLayoutProject>(options: GeometryOperationsOptions<P>) => {
  const imageScaleFor = (elementId: string) => resolveEditorImageScale(frameFor(options.project, elementId), options.activePreviewProfile);
  const autoKeys = options.activeFrame === 'desktop'
    ? ({ frame: 'autoLayoutDesktop', item: 'autoLayoutItemDesktop' } as const)
    : ({ frame: 'autoLayoutMobile', item: 'autoLayoutItemMobile' } as const);
  const autoItemContext = (elementId: string) => {
    const selected = options.definitions.find(candidate => candidate.id === elementId);
    const itemId = selected?.maskHostId ?? elementId;
    const definition = options.definitions.find(candidate => candidate.id === itemId);
    const parentId = definition?.parentId;
    const parentLayout = parentId ? frameFor(options.project, parentId)[autoKeys.frame] : undefined;
    const item = frameFor(options.project, itemId)[autoKeys.item];
    const node = selectedNode(itemId);
    const parentNode = parentId ? selectedNode(parentId) : null;
    if (!parentId || !parentLayout || !item || !node || !parentNode) return null;
    const siblings = options.definitions
      .filter(candidate => candidate.parentId === parentId)
      .map(candidate => ({ id: candidate.id, node: selectedNode(candidate.id), item: frameFor(options.project, candidate.id)[autoKeys.item] }))
      .filter((entry): entry is { id: string; node: HTMLElement; item: NonNullable<WebEditorElementFrame[typeof autoKeys.item]> } => Boolean(entry.node && entry.item && entry.node.parentElement === parentNode))
      .sort((a, b) => a.item.order - b.item.order);
    return { itemId, parentId, parentLayout, node, parentNode, siblings };
  };

  const reorderAutoItem = (elementId: string, delta: number) => {
    const context = autoItemContext(elementId);
    if (!context || !delta) return false;
    const currentIndex = context.siblings.findIndex(entry => entry.id === context.itemId);
    const targetIndex = Math.min(context.siblings.length - 1, Math.max(0, currentIndex + delta));
    if (currentIndex < 0 || targetIndex === currentIndex) return true;
    const reordered = [...context.siblings];
    const [moved] = reordered.splice(currentIndex, 1);
    if (!moved) return true;
    reordered.splice(targetIndex, 0, moved);
    const next = options.cloneProject(options.project);
    reordered.forEach((entry, order) => {
      const frame = frameFor(next, entry.id);
      next.elementFrames[entry.id] = { ...frame, [autoKeys.item]: { ...entry.item, order } };
    });
    options.updateProject(next);
    return true;
  };

  const startAutoReorder = (event: PointerEvent, elementId: string) => {
    const context = autoItemContext(elementId);
    if (!context) return false;
    const originalIndex = context.siblings.findIndex(entry => entry.id === context.itemId);
    if (originalIndex < 0) return true;
    const initialRects = context.siblings.map(entry => editorViewportRect(entry.node));
    let targetIndex = originalIndex;
    const insertion = document.createElement('div');
    insertion.className = 'vp-editor-auto-insertion-line';
    document.body.append(insertion);
    const clearPreview = () => {
      context.siblings.forEach(entry => { entry.node.style.transform = ''; entry.node.style.transition = ''; });
      insertion.remove();
    };
    beginPointerGesture(event, moveEvent => {
      const horizontal = context.parentLayout.flow === 'horizontal';
      const vertical = context.parentLayout.flow === 'vertical';
      if (horizontal || vertical) {
        const position = horizontal ? moveEvent.clientX : moveEvent.clientY;
        targetIndex = initialRects.findIndex(rect => position < (horizontal ? rect.left + rect.width / 2 : rect.top + rect.height / 2));
        if (targetIndex < 0) targetIndex = context.siblings.length - 1;
      } else {
        targetIndex = initialRects.reduce((closest, rect, index) => {
          const distance = Math.hypot(moveEvent.clientX - rect.left - rect.width / 2, moveEvent.clientY - rect.top - rect.height / 2);
          const closestRect = initialRects[closest];
          const closestDistance = closestRect ? Math.hypot(moveEvent.clientX - closestRect.left - closestRect.width / 2, moveEvent.clientY - closestRect.top - closestRect.height / 2) : Number.POSITIVE_INFINITY;
          return distance < closestDistance ? index : closest;
        }, 0);
      }
      const sequence = [...context.siblings];
      const [moved] = sequence.splice(originalIndex, 1);
      if (moved) sequence.splice(targetIndex, 0, moved);
      sequence.forEach((entry, index) => {
        const from = initialRects[context.siblings.findIndex(candidate => candidate.id === entry.id)];
        const to = initialRects[index];
        if (!from || !to) return;
        entry.node.style.transition = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'none' : 'transform 150ms cubic-bezier(.2,.8,.2,1)';
        entry.node.style.transform = `translate3d(${to.left - from.left}px, ${to.top - from.top}px, 0)`;
      });
      const marker = initialRects[targetIndex];
      if (marker) {
        insertion.style.left = `${horizontal ? marker.left - 2 : marker.left}px`;
        insertion.style.top = `${horizontal ? marker.top : marker.top - 2}px`;
        insertion.style.width = `${horizontal ? 4 : marker.width}px`;
        insertion.style.height = `${horizontal ? marker.height : 4}px`;
      }
    }, () => {
      clearPreview();
      const delta = targetIndex - originalIndex;
      if (delta) reorderAutoItem(elementId, delta);
    }, clearPreview);
    return true;
  };

  const setGridColumns = (elementId: string, requestedColumns: number) => {
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    const columns = clampEditorGridColumns(requestedColumns);
    const previousColumns = prepared.columns;
    const parentFrame = frameFor(prepared.nextProject, prepared.parentId);
    prepared.nextProject.elementFrames[prepared.parentId] = {
      ...parentFrame,
      [prepared.properties.container]: true,
      [prepared.properties.columns]: columns,
    };
    for (const [id, frame] of Object.entries(prepared.nextProject.elementFrames)) {
      const placement = frame[prepared.properties.grid];
      if (placement?.parentId !== prepared.parentId) continue;
      const startRatio = (placement.columnStart - 1) / previousColumns;
      const spanRatio = placement.columnSpan / previousColumns;
      const columnStart = Math.min(columns, Math.round(startRatio * columns) + 1);
      const columnSpan = Math.min(columns - columnStart + 1, Math.max(1, Math.round(spanRatio * columns)));
      prepared.nextProject.elementFrames[id] = {
        ...frame,
        [prepared.properties.columns]: columns,
        [prepared.properties.grid]: { ...placement, columnStart, columnSpan },
      };
    }
    options.updateProject(prepared.nextProject);
  };

  const updatePlacement = (elementId: string, update: Partial<WebEditorGridPlacement>) => {
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    const nextPlacement = { ...prepared.placement, ...update };
    nextPlacement.columnStart = Math.min(prepared.columns, Math.max(1, nextPlacement.columnStart));
    nextPlacement.columnSpan = Math.min(
      prepared.columns - nextPlacement.columnStart + 1,
      Math.max(1, nextPlacement.columnSpan),
    );
    prepared.nextProject.elementFrames[elementId] = {
      ...frameFor(prepared.nextProject, elementId),
      [prepared.properties.columns]: prepared.columns,
      [prepared.properties.grid]: nextPlacement,
    };
    if (update.order !== undefined)
      reorderGridItems(prepared.nextProject, prepared.properties, prepared.parentId, elementId, nextPlacement.order);
    options.updateProject(prepared.nextProject);
  };

  const nudgePlacement = (elementId: string, columnDelta: number, orderDelta: number) => {
    if (reorderAutoItem(elementId, orderDelta || columnDelta)) return;
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    const columnStart = Math.min(
      prepared.columns - prepared.placement.columnSpan + 1,
      Math.max(1, prepared.placement.columnStart + columnDelta),
    );
    const placement = {
      ...prepared.placement,
      columnStart,
      order: Math.max(0, prepared.placement.order + orderDelta),
    };
    prepared.nextProject.elementFrames[elementId] = {
      ...frameFor(prepared.nextProject, elementId),
      [prepared.properties.columns]: prepared.columns,
      [prepared.properties.grid]: placement,
    };
    reorderGridItems(prepared.nextProject, prepared.properties, prepared.parentId, elementId, placement.order);
    options.updateProject(prepared.nextProject);
  };

  const adoptLegacyOffset = (elementId: string) => {
    const autoContext = autoItemContext(elementId);
    if (autoContext) {
      const next = options.cloneProject(options.project);
      next.elementFrames[autoContext.itemId] = { ...frameFor(next, autoContext.itemId), x: 0, y: 0 };
      if (autoContext.itemId !== elementId) next.elementFrames[elementId] = { ...frameFor(next, elementId), x: 0, y: 0 };
      options.updateProject(next);
      return;
    }
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    prepared.nextProject.elementFrames[elementId] = {
      ...frameFor(prepared.nextProject, elementId),
      x: 0,
      y: 0,
    };
    options.updateProject(prepared.nextProject);
  };

  const resetLegacyOffset = (elementId: string) => {
    const nextProject = options.cloneProject(options.project);
    nextProject.elementFrames[elementId] = { ...frameFor(nextProject, elementId), x: 0, y: 0 };
    options.updateProject(nextProject);
  };

  const resetGrid = (elementId: string) => {
    const nextProject = options.cloneProject(options.project);
    const properties = viewportProperties(options.activeFrame);
    const frame = { ...frameFor(nextProject, elementId) };
    delete frame[properties.grid];
    delete frame[properties.columns];
    nextProject.elementFrames[elementId] = frame;
    const parentId = options.definitions.find(candidate => candidate.id === elementId)?.parentId;
    if (parentId) {
      const hasSiblingPlacement = options.definitions.some(candidate => {
        if (candidate.parentId !== parentId || candidate.id === elementId) return false;
        return Boolean(nextProject.elementFrames[candidate.id]?.[properties.grid]);
      });
      if (!hasSiblingPlacement) {
        const parentFrame = { ...frameFor(nextProject, parentId) };
        delete parentFrame[properties.container];
        delete parentFrame[properties.columns];
        nextProject.elementFrames[parentId] = parentFrame;
      }
    }
    options.updateProject(nextProject);
  };

  const setFocusPoint = (elementId: string, x: number, y: number) => {
    const nextProject = options.cloneProject(options.project);
    const properties = viewportProperties(options.activeFrame);
    nextProject.elementFrames[elementId] = {
      ...frameFor(nextProject, elementId),
      [properties.focus]: { x: clampEditorFocalPoint(x), y: clampEditorFocalPoint(y) },
    };
    options.updateProject(nextProject);
  };

  const setImageScale = (elementId: string, requestedScale: number) => {
    const nextProject = options.cloneProject(options.project);
    const frame = frameFor(nextProject, elementId);
    nextProject.elementFrames[elementId] = {
      ...frame,
      imageScaleByBreakpoint: { ...frame.imageScaleByBreakpoint, [options.activePreviewProfile]: clampEditorImageScale(requestedScale) },
    };
    options.updateProject(nextProject);
  };

  const resetImageScale = (elementId: string) => {
    const nextProject = options.cloneProject(options.project);
    const frame = { ...frameFor(nextProject, elementId) };
    const sizes = { ...frame.imageScaleByBreakpoint };
    delete sizes[options.activePreviewProfile];
    if (Object.keys(sizes).length) frame.imageScaleByBreakpoint = sizes;
    else delete frame.imageScaleByBreakpoint;
    nextProject.elementFrames[elementId] = frame;
    options.updateProject(nextProject);
  };

  const setImageMaskEnabled = (elementId: string, enabled: boolean) => {
    const nextProject = options.cloneProject(options.project);
    const properties = viewportProperties(options.activeFrame);
    nextProject.elementFrames[elementId] = {
      ...frameFor(nextProject, elementId),
      [properties.mask]: enabled,
      ...(enabled ? { objectFit: frameFor(nextProject, elementId).objectFit ?? 'cover' } : {}),
    };
    options.updateProject(nextProject);
  };

  const previewImageScale = (elementId: string, requestedScale: number) => {
    const node = selectedNode(elementId);
    if (!node) return;
    const frame = frameFor(options.project, elementId);
    const scale = clampEditorImageScale(requestedScale) / 100;
    const properties = viewportProperties(options.activeFrame);
    const selfMask = !options.definitions.find(candidate => candidate.id === elementId)?.maskHostId &&
      (frame[properties.mask] ?? frame.imageMaskDesktop ?? false);
    node.style.scale = String(scale);
    node.style.clipPath = selfMask ? `inset(${((1 - 1 / scale) * 50).toFixed(4)}%)` : '';
  };

  const clearImageScalePreview = (elementId: string) => {
    const node = selectedNode(elementId);
    if (!node) return;
    node.style.scale = '';
    node.style.clipPath = '';
  };

  const setImageMaskSize = (elementId: string, requestedWidth?: number, requestedHeight?: number) => {
    const fluid = prepareFluidImageResize(options, elementId);
    if (fluid) { fluid.update(requestedWidth ?? fluid.startWidth, requestedHeight ?? fluid.startHeight); fluid.commit(); return; }
    const autoMask = autoMaskContext(options, elementId);
    if (autoMask) {
      const nextProject = options.cloneProject(options.project);
      const width = clampEditorGridWidth(requestedWidth ?? autoMask.item.fixedWidth ?? autoMask.hostNode.offsetWidth);
      const height = clampEditorGridHeight(requestedHeight ?? autoMask.item.fixedHeight ?? autoMask.hostNode.offsetHeight);
      nextProject.elementFrames[autoMask.hostId] = {
        ...frameFor(nextProject, autoMask.hostId),
        [autoMask.itemKey]: { ...autoMask.item, widthMode: 'fixed', heightMode: 'fixed', fixedWidth: width, fixedHeight: height },
      };
      const imageFrame = frameFor(nextProject, elementId);
      nextProject.elementFrames[elementId] = { ...imageFrame, objectFit: imageFrame.objectFit ?? 'cover' };
      options.updateProject(nextProject);
      return;
    }
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    const parentWidth = Math.max(24, prepared.parentNode.offsetWidth);
    const maskWidth = Math.min(parentWidth, clampEditorGridWidth(requestedWidth ?? prepared.placement.maskWidth ?? prepared.node.offsetWidth));
    const maskHeight = clampEditorGridHeight(
      requestedHeight ?? prepared.placement.maskHeight ?? prepared.node.offsetHeight,
    );
    const columnSpan = Math.min(
      prepared.columns - prepared.placement.columnStart + 1,
      Math.max(1, Math.ceil((maskWidth / parentWidth) * prepared.columns)),
    );
    const imageFrame = frameFor(prepared.nextProject, elementId);
    prepared.nextProject.elementFrames[elementId] = {
      ...imageFrame,
      objectFit: imageFrame.objectFit ?? 'cover',
      [prepared.properties.columns]: prepared.columns,
      [prepared.properties.grid]: { ...prepared.placement, columnSpan, maskWidth, maskHeight },
    };
    options.updateProject(prepared.nextProject);
  };

  const startMove = (event: PointerEvent, elementId: string) => {
    if (startAutoReorder(event, elementId)) return;
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    const startPointer = { x: event.clientX, y: event.clientY };
    const parentRect = editorViewportRect(prepared.parentNode);
    const startPlacement = { ...prepared.placement };
    let nextPlacement = { ...startPlacement };
    const previewNode = prepared.node;
    const overlay = overlayNode();
    beginPointerGesture(
      event,
      moveEvent => {
        const dx = moveEvent.clientX - startPointer.x;
        const dy = moveEvent.clientY - startPointer.y;
        const columnDelta = Math.round((dx / Math.max(1, parentRect.width)) * prepared.columns);
        nextPlacement = {
          ...startPlacement,
          columnStart: Math.min(
            prepared.columns - startPlacement.columnSpan + 1,
            Math.max(1, startPlacement.columnStart + columnDelta),
          ),
          order: Math.max(0, startPlacement.order + Math.round(dy / Math.max(48, editorViewportRect(previewNode).height))),
        };
        const snappedX = ((nextPlacement.columnStart - startPlacement.columnStart) / prepared.columns) * parentRect.width;
        previewNode.style.translate = `${snappedX / Math.max(0.01, parentRect.width / prepared.parentNode.offsetWidth)}px ${dy / Math.max(0.01, parentRect.width / prepared.parentNode.offsetWidth)}px`;
        if (overlay) overlay.style.transform = `translate3d(${snappedX}px, ${dy}px, 0)`;
      },
      () => {
        previewNode.style.translate = '';
        if (overlay) overlay.style.transform = '';
        prepared.nextProject.elementFrames[elementId] = {
          ...frameFor(prepared.nextProject, elementId),
          [prepared.properties.columns]: prepared.columns,
          [prepared.properties.grid]: nextPlacement,
        };
        reorderGridItems(prepared.nextProject, prepared.properties, prepared.parentId, elementId, nextPlacement.order);
        options.updateProject(prepared.nextProject);
      },
      () => {
        previewNode.style.translate = '';
        if (overlay) overlay.style.transform = '';
      },
    );
  };

  const startResize = (event: PointerEvent, elementId: string, handle: EditorResizeHandle) => {
    const fluid = prepareFluidImageResize(options, elementId);
    if (fluid) {
      const start = { x: event.clientX, y: event.clientY };
      const scale = editorViewportRect(fluid.host).width / Math.max(1, fluid.startWidth);
      beginPointerGesture(event, move => {
        const dx = (move.clientX - start.x) / Math.max(0.01, scale), dy = (move.clientY - start.y) / Math.max(0.01, scale);
        const width = Math.max(24, fluid.startWidth + (handle.includes('e') ? dx : handle.includes('w') ? -dx : 0));
        const height = handle.length === 2 && !move.shiftKey ? width * fluid.startHeight / fluid.startWidth : Math.max(24, fluid.startHeight + (handle.includes('s') ? dy : handle.includes('n') ? -dy : 0));
        fluid.update(width, height);
      }, fluid.commit, fluid.restore);
      return;
    }
    const prepared = prepareGridProject(options, elementId);
    if (!prepared) return;
    const startPointer = { x: event.clientX, y: event.clientY };
    const nodeRect = editorViewportRect(prepared.node);
    const parentRect = editorViewportRect(prepared.parentNode);
    const previewScale = parentRect.width / Math.max(1, prepared.parentNode.offsetWidth);
    const startPlacement = { ...prepared.placement };
    const startWidth = startPlacement.maskWidth ?? prepared.node.offsetWidth;
    const startHeight = startPlacement.maskHeight ?? prepared.node.offsetHeight;
    const aspectRatio = nodeRect.width / Math.max(1, nodeRect.height);
    let nextPlacement = {
      ...startPlacement,
      maskWidth: clampEditorGridWidth(startWidth),
      maskHeight: clampEditorGridHeight(startHeight),
    };
    const overlay = overlayNode();
    beginPointerGesture(
      event,
      moveEvent => {
        const dx = moveEvent.clientX - startPointer.x;
        const dy = moveEvent.clientY - startPointer.y;
        const pageDx = dx / Math.max(0.01, previewScale);
        const pageDy = dy / Math.max(0.01, previewScale);
        let columnStart = startPlacement.columnStart;
        let columnSpan = startPlacement.columnSpan;
        let maskWidth = startWidth;
        if (handle.includes('e')) maskWidth = startWidth + pageDx;
        if (handle.includes('w')) {
          maskWidth = startWidth - pageDx;
        }
        maskWidth = Math.min(prepared.parentNode.offsetWidth, clampEditorGridWidth(maskWidth));
        columnSpan = Math.max(
          1,
          Math.ceil((maskWidth / Math.max(1, prepared.parentNode.offsetWidth)) * prepared.columns),
        );
        if (handle.includes('w')) columnStart = Math.max(1, startPlacement.columnStart + startPlacement.columnSpan - columnSpan);
        columnSpan = Math.min(prepared.columns - columnStart + 1, columnSpan);
        let maskHeight = startHeight;
        if (handle === 'n' || handle === 's') maskHeight = startHeight + (handle === 'n' ? -pageDy : pageDy);
        else if (handle.length === 2) {
          maskHeight = maskWidth / Math.max(0.1, aspectRatio);
        }
        nextPlacement = {
          ...startPlacement,
          columnStart,
          columnSpan,
          maskWidth,
          maskHeight: clampEditorGridHeight(maskHeight),
        };
        prepared.node.style.width = `${maskWidth}px`;
        prepared.node.style.height = `${nextPlacement.maskHeight}px`;
        if (overlay) {
          overlay.style.width = `${maskWidth * previewScale}px`;
          overlay.style.height = `${nextPlacement.maskHeight * previewScale}px`;
        }
      },
      () => {
        prepared.node.style.width = '';
        prepared.node.style.height = '';
        if (overlay) {
          overlay.style.width = '';
          overlay.style.height = '';
        }
        const imageFrame = frameFor(prepared.nextProject, elementId);
        prepared.nextProject.elementFrames[elementId] = {
          ...imageFrame,
          objectFit: imageFrame.objectFit ?? 'cover',
          [prepared.properties.columns]: prepared.columns,
          [prepared.properties.grid]: nextPlacement,
        };
        options.updateProject(prepared.nextProject);
      },
      () => {
        prepared.node.style.width = '';
        prepared.node.style.height = '';
        if (overlay) {
          overlay.style.width = '';
          overlay.style.height = '';
        }
      },
    );
  };

  const startImageScale = (event: PointerEvent, elementId: string, handle: EditorResizeHandle) => {
    const node = selectedNode(elementId);
    if (!node) return;
    const nextProject = options.cloneProject(options.project);
    const frame = frameFor(nextProject, elementId);
    const properties = viewportProperties(options.activeFrame);
    const startScale = resolveEditorImageScale(frame, options.activePreviewProfile);
    const startPointer = { x: event.clientX, y: event.clientY };
    const startRect = editorViewportRect(node);
    const baseWidth = startRect.width / Math.max(0.25, startScale / 100);
    const baseHeight = startRect.height / Math.max(0.25, startScale / 100);
    const directionX = handle.includes('w') ? -1 : 1;
    const directionY = handle.includes('n') ? -1 : 1;
    const selfMask = !options.definitions.find(candidate => candidate.id === elementId)?.maskHostId &&
      (frame[properties.mask] ?? frame.imageMaskDesktop ?? false);
    const overlay = overlayNode();
    let scale = startScale;
    const clearPreview = () => {
      node.style.scale = '';
      node.style.clipPath = '';
      if (overlay) {
        overlay.style.left = '';
        overlay.style.top = '';
        overlay.style.width = '';
        overlay.style.height = '';
      }
    };
    beginPointerGesture(
      event,
      moveEvent => {
        const dx = (moveEvent.clientX - startPointer.x) * directionX;
        const dy = (moveEvent.clientY - startPointer.y) * directionY;
        const diagonal = Math.max(80, Math.hypot(baseWidth, baseHeight));
        scale = clampEditorImageScale(startScale + ((dx + dy) / diagonal) * 100);
        const factor = scale / 100;
        node.style.scale = String(factor);
        if (selfMask) node.style.clipPath = `inset(${((1 - 1 / factor) * 50).toFixed(4)}%)`;
        if (overlay) {
          const width = baseWidth * factor;
          const height = baseHeight * factor;
          overlay.style.left = `${startRect.left + (startRect.width - width) / 2}px`;
          overlay.style.top = `${startRect.top + (startRect.height - height) / 2}px`;
          overlay.style.width = `${width}px`;
          overlay.style.height = `${height}px`;
        }
      },
      () => {
        clearPreview();
        nextProject.elementFrames[elementId] = { ...frame, imageScaleByBreakpoint: { ...frame.imageScaleByBreakpoint, [options.activePreviewProfile]: scale } };
        options.updateProject(nextProject);
      },
      clearPreview,
    );
  };

  const startFocus = (event: PointerEvent, elementId: string) => {
    const node = selectedNode(elementId);
    if (!node) return;
    const nextProject = options.cloneProject(options.project);
    const properties = viewportProperties(options.activeFrame);
    let point = frameFor(nextProject, elementId)[properties.focus] ?? { x: 50, y: 50 };
    const updatePoint = (moveEvent: PointerEvent) => {
      const rect = editorViewportRect(node);
      point = {
        x: clampEditorFocalPoint(((moveEvent.clientX - rect.left) / Math.max(1, rect.width)) * 100),
        y: clampEditorFocalPoint(((moveEvent.clientY - rect.top) / Math.max(1, rect.height)) * 100),
      };
      node.style.objectPosition = `${point.x}% ${point.y}%`;
      const marker = document.querySelector<HTMLElement>('.vp-editor-image-focus-marker');
      if (marker) {
        marker.style.left = `${point.x}%`;
        marker.style.top = `${point.y}%`;
      }
    };
    beginPointerGesture(
      event,
      updatePoint,
      () => {
        node.style.objectPosition = '';
        nextProject.elementFrames[elementId] = { ...frameFor(nextProject, elementId), [properties.focus]: point };
        options.updateProject(nextProject);
      },
      () => {
        node.style.objectPosition = '';
      },
    );
  };

  return {
    imageScaleFor,
    setGridColumns,
    updatePlacement,
    nudgePlacement,
    adoptLegacyOffset,
    resetLegacyOffset,
    resetGrid,
    setFocusPoint,
    setImageScale,
    resetImageScale,
    setImageMaskEnabled,
    previewImageScale,
    clearImageScalePreview,
    setImageMaskSize,
    startMove,
    startResize,
    startImageScale,
    startFocus,
  };
};

export type EditorGeometryOperations = ReturnType<typeof createEditorGeometryOperations>;
